import { Box, Flex, Text, Spinner, Tooltip } from '@chakra-ui/react';
import { faUserPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonText,
  ClearButton,
  OutlineCard,
  Pagination,
  Table,
  useToast,
  Switch
} from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { getJournalBoardMembers } from 'src/api/queries';
import AddBoardMembersModal from './AddBoardMembersModal';
import RemoveBoardMemberModal from './RemoveBoardMemberModal';
import {
  removeJournalBoardMembers,
  useUpdateJournalSettings
} from 'src/api/mutations';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import { FieldLabel } from 'src/components/Field';

interface Props {
  journalId: string;
  organizationId: string;
  journalName: string;
  isBoardMemberReviewerAssignmentEnabled?: boolean;
}

const LIMIT = 10;

const BoardMembersSection: React.FC<Props> = ({
  journalId,
  organizationId,
  journalName,
  isBoardMemberReviewerAssignmentEnabled
}) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [removeModal, setRemoveModal] = useState({
    isOpen: false,
    userId: ''
  });
  const [profileModal, setProfileModal] = useState({
    isOpen: false,
    userId: ''
  });
  const [page, setPage] = useState(1);

  const [isBoardMemeberEnabled, setBoardMemberEnabled] = useState(
    !!isBoardMemberReviewerAssignmentEnabled
  );
  const removeBoardMemberMutation = removeJournalBoardMembers(journalId);
  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const { data, isLoading } = useQuery({
    queryKey: ['submission-context-board-members', { id: journalId }, page],
    queryFn: () =>
      getJournalBoardMembers(journalId, {
        offset: (page - 1) * LIMIT,
        limit: LIMIT
      }),
    retry: 0
  });
  const toggleModal = () => setIsOpen(prev => !prev);

  const handleToggleBoardMemberAssignment = async (checked: boolean) => {
    setBoardMemberEnabled(checked);

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: {
        boardMemberReviewerAssigment: checked
      }
    } as any);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: checked
          ? 'Board Member Assignment enabled'
          : 'Board Member Assignment disabled',
        isClosable: true
      });
      return;
    }

    // Revert on failure
    setBoardMemberEnabled(!checked);
    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string' ? res.message : 'Something went wrong',
      isClosable: true
    });
  };

  const total = data?.response?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  const hasBoardMembers = total > 0;

  /**
   * While the flag is on and members are assigned it can no longer be switched
   * off — that would strand them, so the members have to be removed first.
   * Enabling the flag is never blocked, including when it is already off.
   */
  const isDisableLocked = isBoardMemeberEnabled && hasBoardMembers;

  const onRemove = async () => {
    const res = await removeBoardMemberMutation.mutateAsync(removeModal.userId);
    if (res && res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Board Member Removed from Journal',
        isClosable: true
      });
      return setRemoveModal({ isOpen: false, userId: '' });
    }

    return toast({
      variant: 'error',
      title: res?.message?.error || 'Unexpected error.',
      isClosable: true
    });
  };

  return (
    <>
      {isOpen && (
        <AddBoardMembersModal
          isOpen
          onClose={toggleModal}
          journalId={journalId}
          organizationId={organizationId}
        />
      )}

      {profileModal.isOpen && (
        <SubmittedAuthorDetailsModal
          backButtonText="Back"
          userId={profileModal.userId}
          onBackClick={() => setProfileModal({ isOpen: false, userId: '' })}
          isProxyUserNotRequired={true}
        />
      )}

      <RemoveBoardMemberModal
        isOpen={removeModal.isOpen}
        onClose={() => setRemoveModal({ isOpen: false, userId: '' })}
        onRemove={onRemove}
        isLoading={removeBoardMemberMutation.isLoading}
      />

      <OutlineCard borderless paddingX={4} paddingY={6} mb={2}>
        <Flex alignItems="center" justifyContent="space-between">
          {/* configure save path : config.globalLexiconConfig */}

          <FieldLabel
            label="Enable Board Member Assignment"
            subText="Allow board members to be assigned to review submissions without an invitation."
            isRequired={false}
          />
          <Tooltip
            shouldWrapChildren
            isDisabled={!isDisableLocked}
            hasArrow
            label="To disable board member assignment, first remove all currently assigned board members."
            fontSize="md"
            placement="left"
          >
            <Switch
              isChecked={isBoardMemeberEnabled}
              isDisabled={isDisableLocked}
              onChange={event => {
                handleToggleBoardMemberAssignment(event.target.checked);
              }}
              mt={2}
            />
          </Tooltip>
        </Flex>
      </OutlineCard>
      {isBoardMemeberEnabled && (
        <Flex flexDirection={'column'}>
          <OutlineCard borderless paddingX={4} paddingY={6}>
            <Flex justifyContent={'space-between'} alignItems={'center'}>
              <Text textStyle={'headingSection'}>Board Members</Text>
              <Button
                iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
                onClick={toggleModal}
              >
                Add Board Members
              </Button>
            </Flex>
            {isLoading ? (
              <Spinner />
            ) : !data?.response?.items?.length ? (
              <Flex
                flexDirection={'column'}
                gap={2}
                marginTop={4}
                alignItems="flex-start"
              >
                <Flex
                  width={'100%'}
                  padding={8}
                  justifyContent="center"
                  background="neutralSecondaryFill"
                  borderRadius="lg"
                  fontSize="48px"
                >
                  <FontAwesomeIcon icon={faUserPlus} />
                </Flex>

                <Text textStyle="headingGroup" marginTop={4}>
                  No Board Members Added
                </Text>
                <Text textStyle="textBody">
                  Assign board members directly to review submissions without an
                  invitation.
                </Text>
              </Flex>
            ) : (
              <>
                <Text textStyle="textBody">
                  {total} board members assigned to {journalName}
                </Text>

                <Box overflowX="auto" mt={2} width="100%">
                  <Table>
                    <Table.HeaderRow gap={2}>
                      <Table.HeaderCell flex="5">Board Member</Table.HeaderCell>
                      <Table.HeaderCell flex="6">Email</Table.HeaderCell>
                      <Table.HeaderCell flex="2"></Table.HeaderCell>
                    </Table.HeaderRow>
                    <Table.Body>
                      {data.response.items.map(item => (
                        <Table.Row
                          key={item.email}
                          gap={2}
                          _last={{ borderBottom: 'none' }}
                        >
                          <Table.RowCell flex="5" paddingY={2}>
                            {item.status === 'active' ? (
                              <ClearButton
                                width={'100%'}
                                justifyContent={'flex-start'}
                                ariaLabel={`Open Profile ${item.email}`}
                                onClick={() =>
                                  setProfileModal({
                                    isOpen: true,
                                    userId: item.id
                                  })
                                }
                              >
                                <Text textStyle="textBody">
                                  {item.firstName} {item.lastName}
                                </Text>
                              </ClearButton>
                            ) : (
                              <Text textStyle="textBody">
                                {item.firstName} {item.lastName}
                              </Text>
                            )}
                          </Table.RowCell>
                          <Table.RowCell flex="6" paddingY={2}>
                            {item.email}
                          </Table.RowCell>
                          <Table.RowCell flex="2" paddingY={2}>
                            <ButtonText
                              variant="caution"
                              leftIcon={<FontAwesomeIcon icon={faTrash} />}
                              onClick={() =>
                                setRemoveModal({
                                  isOpen: true,
                                  userId: item.id
                                })
                              }
                            >
                              Delete
                            </ButtonText>
                          </Table.RowCell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Box>

                {totalPages > 1 && (
                  <Flex justifyContent="center" marginTop={4}>
                    <Pagination
                      ariaLabel="Pagination"
                      currentPage={page}
                      totalPages={totalPages}
                      onPageChange={setPage}
                    />
                  </Flex>
                )}
              </>
            )}
          </OutlineCard>
        </Flex>
      )}
    </>
  );
};

export default BoardMembersSection;
